import { useEffect, useState, useRef, useContext } from 'react';
import { Helmet } from 'react-helmet-async'
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Container,
  Grid,
  Typography,
  Avatar,
  Box,
  Card,
  CardHeader,
  CardContent,
  Stack,
  CircularProgress,
  IconButton,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { Edit, AddPhotoAlternate } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import format from 'date-fns/format'

import ACL from 'components/ACL'
import Breadcrumbs from 'components/Breadcrumbs';
import ProfileField from 'components/ProfileField'
import ImageGallery from 'components/ImageGallery'
import EditProfile from 'sections/dialogs/editProfile'
import Iconify from 'components/Iconify';

import { UserContext } from 'contexts';
import { getUser, updateUser, deleteFile, uploadFile } from 'services/requests/user'
import { toast, setMask } from 'utils'
import { LoadingButton } from '@mui/lab';

export default function ClientDetail() {
  const { t } = useTranslation('common');
  const userContext = useContext(UserContext);
  const { state } = userContext;
  const { id } = useParams();
  const navigate = useNavigate();
  const inputFilePersonalDocumentRef = useRef(null);
  const inputFilePersonalLocationRef = useRef(null);

  const { pathname } = useLocation();
  const breadcrumbFromProvider = pathname.includes('todos')

  const [data, setData] = useState({});
  const [loadingUploadPersonalDocument, setLoadingUploadPersonalDocument] = useState(false);
  const [loadingUploadPersonalLocation, setLoadingUploadPersonalLocation] = useState(false);
  const [loadingActive, setLoadingActive] = useState(false);
  const [selectedFormEdit, setSelectedFormEdit] = useState(null);

  const handleSelectEditForm = index => {
    if (index === 0) {
      setSelectedFormEdit({ index, form: data.profile })
    } else if (index === 1) {
      setSelectedFormEdit({ index, form: data.address })
    } else if (index === 2) {
      const {
        name: socialName,
        email: emailCompany,
        phone: phoneCompany,
        ...restCompany
      } = data.company

      setSelectedFormEdit({ index, form: { ...restCompany, socialName, emailCompany, phoneCompany } })
    } else if (index === 3) {
      const {
        zipcode: zipcodeCompany,
        street: streetCompany,
        neighborhood: neighborhoodCompany,
        city: cityCompany,
        state: stateCompany,
        number: numberCompany,
        complement: complementCompany,
      } = data.companyaddress

      setSelectedFormEdit({ index, form: { zipcodeCompany, streetCompany, neighborhoodCompany, cityCompany, stateCompany, numberCompany, complementCompany } })
    } else if (index === 4) {
      const {
        observation
      } = data.profile

      setSelectedFormEdit({ index, form: { observation } })
    } else if (index === 5) {
      const {
        location
      } = data.profile

      setSelectedFormEdit({ index, form: { location } })
    }
  }

  const handleAddPersonalDocument = () => {
    inputFilePersonalDocumentRef.current.click()
  }

  const handleAddPersonalLocation = () => {
    inputFilePersonalLocationRef.current.click()
  }

  const handleSelectFileToAdd = (file, type) => {
    if (type === 'personalDocument') {
      setLoadingUploadPersonalDocument(true)
    } else {
      setLoadingUploadPersonalLocation(true)
    }

    const formData = new FormData()
    formData.append('file', file)
    formData.append('body', JSON.stringify({ type }))

    uploadFile({ id, formData })
      .then(() => {
        toast('Envio realizado com successo!', 'success')
        getData()
      })
      .catch((err) => toast(err, 'error'))
      .finally(() => {
        if (type === 'personalDocument') {
          setLoadingUploadPersonalDocument(false)
          inputFilePersonalDocumentRef.current.value = null
        } else {
          setLoadingUploadPersonalLocation(false)
          inputFilePersonalLocationRef.current.value = null
        }
      })
  }

  const handleRemoveFile = key => {
    deleteFile({ key })
      .then(() => getData())
      .catch(err => toast(err, 'error'))
  }

  const isSellerClient = state?.data?.role === 'seller';

  const getProfileFields = () => {
    const fields = isSellerClient
      ? [
          'name',
          {
            key: 'phone',
            customValue: value => (
              <>
                <a href={`https://wa.me/+55${value}`} target="_blank" rel="noreferrer">
                  {setMask('(99) 99999-9999', value)}
                </a>
                <Iconify icon="mingcute:whatsapp-fill" width={15} sx={{ ml: 1 }} />
              </>
            )
          }
        ]
      : [
          'name',
          'email',
          {
            key: 'phone',
            customValue: value => (
              <>
                <a href={`https://wa.me/+55${value}`} target="_blank" rel="noreferrer">
                  {setMask('(99) 99999-9999', value)}
                </a>
                <Iconify icon="mingcute:whatsapp-fill" width={15} sx={{ ml: 1 }} />
              </>
            )
          },
          { key: 'cpf', customValue: value => setMask('999.999.999-99', value) },
          'rg',
          'birthDate',
          'profession',
          { key: 'maritalStatus', customValue: value => t(`enum.maritalStatus.${value}`) },
          { key: 'propertyRegime', customValue: value => t(`enum.propertyRegime.${value}`) },
          'nationality'
        ];

    return (
      <Box>
        {fields.map((item, key) => {
          const label = typeof item === 'string' ? item : item.key;
          const value = typeof item === 'string'
            ? data.profile[label]
            : item.customValue(data.profile[label]);

          return (
            <ProfileField
              key={key}
              label={t(`myProfile.fields.profile.${label}`)}
              value={value}
            />
          );
        })}

        {!isSellerClient && (
          <>
            <CardHeader
              title={t('myProfile.address')}
              sx={{ pl: 0, pr: 0, pb: 3 }}
              action={(
                <ACL profiles={['admin']}>
                  <IconButton color="inherit" onClick={() => handleSelectEditForm(1)}>
                    <Edit />
                  </IconButton>
                </ACL>
              )}
            />
            <ProfileField
              value={(
                <>
                  {`${data.address.street}, ${data.address.number}`}
                  <br />
                  {`${data.address.neighborhood}, ${data.address.city}/${data.address.state}`}
                  <br />
                  {setMask('CEP 99999-999', data.address.zipcode)}
                  {data.address.complement && <><br />{data.address.complement}</>}
                </>
              )}
            />
          </>
        )}
      </Box>
    );
  };

  const getCompanyFields = () => {
    const fields = isSellerClient
      ? [
          {
            key: 'phone',
            source: 'profile',
            customValue: value => (
              <>
                <a href={`https://wa.me/+55${value}`} target="_blank" rel="noreferrer">
                  {setMask('(99) 99999-9999', value)}
                </a>
                <Iconify icon="mingcute:whatsapp-fill" width={15} sx={{ ml: 1 }} />
              </>
            )
          },
          'name',
          'fantasyName',
          { key: 'cnpj', customValue: value => setMask('99.999.999/9999-99', value) },
          { key: 'pixType', customValue: value => t(`enum.pixType.${value}`) },
          'pix'
        ]
      : [
          'name',
          'fantasyName',
          'email',
          {
            key: 'phone',
            customValue: value => (
              <>
                <a href={`https://wa.me/+55${value}`} target="_blank" rel="noreferrer">
                  {setMask('(99) 99999-9999', value)}
                </a>
                <Iconify icon="mingcute:whatsapp-fill" width={15} sx={{ ml: 1 }} />
              </>
            )
          },
          { key: 'cnpj', customValue: value => setMask('99.999.999/9999-99', value) },
          { key: 'pixType', customValue: value => t(`enum.pixType.${value}`) },
          'pix'
        ];

    return (
      <Box>
        {fields.map((item, key) => {
          const config = typeof item === 'string' ? { key: item } : item;
          const source = config.source === 'profile' ? data.profile : data.company;
          const value = config.customValue
            ? config.customValue(source[config.key])
            : source[config.key];

          const labelKey =
            config.source === 'profile'
              ? `myProfile.fields.profile.${config.key}`
              : `myProfile.fields.company.${config.key}`;

          return (
            <ProfileField
              key={key}
              label={t(labelKey)}
              value={value}
            />
          );
        })}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 2
            }}
          >
            <Box sx={{ flex: 1 }}>
              <ProfileField
                label={t('myProfile.address')}
                value={(
                  <>
                    {`${data.companyaddress.street}, ${data.companyaddress.number}`}
                    <br />
                    {`${data.companyaddress.neighborhood}, ${data.companyaddress.city}/${data.companyaddress.state}`}
                    <br />
                    {setMask('CEP 99999-999', data.companyaddress.zipcode)}
                    {data.companyaddress.complement && (
                      <>
                        <br />
                        {data.companyaddress.complement}
                      </>
                    )}
                  </>
                )}
              />
            </Box>

            <ACL profiles={['admin']}>
              <IconButton
                size="small"
                color="inherit"
                onClick={() => handleSelectEditForm(3)}
              >
                <Edit />
              </IconButton>
            </ACL>
          </Box>
      </Box>
    );
  };

  const handleActiveUser = active => {
    setLoadingActive(true)

    updateUser({
      id,
      body: { user: { active } }
    })
      .then(() => toast(`Perfil ${active ? 'ativado' : 'inativado'} com sucesso!`, 'success'))
      .catch((err) => toast(err, 'error'))
      .finally(() => setLoadingActive(false))
  }

  const getData = () => {
    getUser({ id })
      .then((res) => {
        const { address, company, companyaddress, provider, files, ...profile } = res;
        profile.birthDate = format(new Date(profile.birthDate), 'dd/MM/yyyy')
        const documentFiles = [], locationFiles = []
        files.forEach(item => (item.type === 'personalDocument' ? documentFiles : locationFiles).push(item))
        setData({
          profile, address, company, companyaddress, documentFiles, locationFiles, provider
        })
      })
      .catch(() => navigate('/', { replace: true }))
  }

  useEffect(() => {
    getData()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const breadcrumbItems = state?.data?.role === 'assistant' || state?.data?.role === 'legal' ? [
    { label: t('clientDetail.title') }
  ] : [
    { path: `/clientes${breadcrumbFromProvider ? '/todos' : ''}`, label: t(`clients${breadcrumbFromProvider ? 'Provider' : 'Invited'}.title`) },
    { label: t('clientDetail.title') }
  ]

  return (
    <>
      <Helmet>
        <title>{t('clientDetail.pageTitle')} - { process.env.REACT_APP_PROJECT_NAME }</title>
      </Helmet>

      <Box component="main" mt={1}>
        {
          data?.profile?.id ? (
            <Container maxWidth="lg">
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
                <Breadcrumbs
                  items={[...breadcrumbItems]}
                />
              </Stack>

              <Card>
                <CardContent>
                  <Box sx={{ alignItems: 'center', display: 'flex', flexDirection: 'column' }}>
                    <Avatar alt={data.profile.name} sx={{ height: 80, mb: 2, width: 80 }}>
                      {data.profile.name?.slice(0, 1)}
                    </Avatar>
                    <Typography gutterBottom variant="h5">
                      {data.profile.name}
                    </Typography>
                    <Typography color="text.secondary" variant="body2">
                      {t(`profiles.${data.profile.role}`)}
                    </Typography>
                    <Typography color="text.secondary" variant="body2">
                      {data.provider.name}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              <Grid container rowSpacing={2} columnSpacing={2} mt={1} mb={3}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardHeader
                      title={t('myProfile.personal')}
                      action={(
                        <ACL profiles={['admin']}>
                          <IconButton color="inherit" onClick={() => handleSelectEditForm(0)}>
                            <Edit />
                          </IconButton>
                        </ACL>
                      )}
                    />
                    <CardContent>
                      {
                        getProfileFields()
                      }
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardHeader
                      title={t('myProfile.company')}
                      action={(
                        <ACL profiles={['admin']}>
                          <IconButton color="inherit" onClick={() => handleSelectEditForm(2)}>
                            <Edit />
                          </IconButton>
                        </ACL>
                      )}
                    />
                    <CardContent>
                      {
                        getCompanyFields()
                      }
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {state?.data?.role !== 'seller' && (
                <Card>
                  <CardHeader
                    title={t('myProfile.docs')}
                    action={(
                      <ACL profiles={['admin', 'legal']}>
                        <input
                          ref={inputFilePersonalDocumentRef}
                          accept="image/png, image/jpeg, image/gif, image/webp, application/pdf"
                          id="document"
                          type="file"
                          hidden
                          onChange={e => handleSelectFileToAdd(e.target.files[0], 'personalDocument')}
                        />
                        {loadingUploadPersonalDocument ? 'Enviando documento...' : null}
                        <LoadingButton
                          color="inherit"
                          onClick={handleAddPersonalDocument}
                          loading={loadingUploadPersonalDocument}
                        >
                          <AddPhotoAlternate />
                        </LoadingButton>
                      </ACL>
                    )}
                  />
                  <CardContent sx={{ pt: 5 }}>
                    {data.documentFiles?.length > 0 ? (
                      <ImageGallery
                        files={data.documentFiles}
                        folder={process.env.REACT_APP_S3_FOLDER_USERDOCS}
                        onRemoveFile={handleRemoveFile}
                      />
                    ) : (
                      <Box sx={{ alignItems: 'center', display: 'flex', flexDirection: 'column' }}>
                        <Box
                          component="img"
                          src="/assets/illustrations/illustration_empty_content.svg"
                          sx={{ height: 160, mx: 'auto', mb: 5 }}
                        />
                        <Typography variant="h6" paragraph mb={3}>
                          {t('no_data')}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )}

              <ACL profiles={['admin', 'seller', 'legal']}>
                <Card sx={{ mt: 3 }}>
                  <CardHeader
                    title={t('myProfile.observation')}
                    action={(
                      <ACL profiles={['admin', 'legal']}>
                        <IconButton color="inherit" onClick={() => handleSelectEditForm(4)}>
                          <Edit />
                        </IconButton>
                      </ACL>
                    )}
                  />
                  <CardContent sx={{ pt: 2 }}>
                    <Typography variant="body1" className="ellipsis">
                      {data.profile?.observation || t('myProfile.observationEmpty')}
                    </Typography>
                  </CardContent>
                </Card>
              </ACL>

              <ACL profiles={['admin', 'seller', 'assistant', 'legal']}>
                <Card sx={{ mt: 3 }}>
                  <CardHeader
                    title={t('myProfile.location')}
                    action={(
                      <ACL profiles={['admin', 'legal']}>
                        {
                          data.locationFiles?.length ? null : (
                            <>
                              <input
                                ref={inputFilePersonalLocationRef}
                                accept="image/png, image/jpeg, image/gif, image/webp"
                                id="location"
                                type="file"
                                hidden
                                onChange={e => handleSelectFileToAdd(e.target.files[0], 'personalLocation')}
                              />
                              {
                                loadingUploadPersonalLocation ? 'Enviando imagem...' : null
                              }
                              <LoadingButton
                                color="inherit"
                                onClick={() => handleAddPersonalLocation()}
                                loading={loadingUploadPersonalLocation}
                              >
                                <AddPhotoAlternate />
                              </LoadingButton>
                            </>
                          )
                        }
                        <IconButton color="inherit" onClick={() => handleSelectEditForm(5)}>
                          <Edit />
                        </IconButton>
                      </ACL>
                    )}
                  />
                  <CardContent sx={{ pt: 2 }}>
                    <Typography variant="body1" className="ellipsis">
                      {data.profile?.location || t('myProfile.locationEmpty')}
                    </Typography>

                    {
                      data.locationFiles?.length > 0 ? (
                        <Box sx={{ mt: 3 }}>
                          <ImageGallery
                            files={data.locationFiles || []}
                            folder={process.env.REACT_APP_S3_FOLDER_USERDOCS}
                            onRemoveFile={handleRemoveFile}
                          />
                        </Box>
                      ) : null
                    }
                  </CardContent>
                </Card>
              </ACL>

              <ACL profiles={['admin']}>
                <Card sx={{ mt: 3 }}>
                  <CardHeader
                    title={t('myProfile.settings')}
                  />
                  <CardContent>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="active"
                          onChange={e => handleActiveUser(e.target.checked)}
                          defaultChecked={data.profile.active}
                          disabled={loadingActive}
                        />
                      }
                      label={t('myProfile.active')}
                    />
                  </CardContent>
                </Card>
              </ACL>
            </Container>
          ) : (
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={80} sx={{ my: 12 }} />
            </Box>
          )
        }

        {
          selectedFormEdit?.index >= 0 && selectedFormEdit?.form ? (
            <EditProfile
              id={id}
              formIndex={selectedFormEdit.index}
              defaultValues={selectedFormEdit.form}
              onSuccess={() => {
                setSelectedFormEdit(null)
                getData()
              }}
              onCancel={() => setSelectedFormEdit(null)}
            />
          ) : null
        }
      </Box>
    </>
  );
}
